import { HsVector3 } from '../../math-new/objects/vector3.class';
import { HsGlobal } from '../global';
import type { HsCameraTool } from './camera-tool.interface';
import type { HsCamera } from '../../engine-new/objects/cameras/camera.class';
import { HsControlActionType } from '../actions/control-action-type.enum';
import type { HsControlAction } from '../actions/control-action.abstract';
import type { HsCanvasSize } from '../utils/canvas-size.class';
import { HsScrollControlAction } from '../actions/scroll-control-action.class';
import { HsZoomControlAction } from '../actions/zoom-control-action.class';
import { HsMouseMoveControlAction } from '../actions/mouse-move-control-action.class';
import { HsMatrix4 } from '../../math-new/objects/matrix4.class';
import { hsApplyTransformationToCamera } from './apply-transformation-to-camera.function';
import { HsQuaternion } from '../../math-new/objects/quaternion.class';
import { HsToolsConstants } from '../tools-constants.constants';
import { HsHypersceneConstants } from '../../math-new/constants/hyperscene-constants.global';

const CAMERA_TO_TARGET_MIN_DISTANCE: number = 0.5;

export class HsCameraRotationAroundSelectedPoint implements HsCameraTool {
  private readonly rotationPoint: HsVector3 = HsGlobal.ROTATE_POINT;

  private frameStartTime: number | null = null;

  private frameEndTime: number | null = null;

  private deltaTime: number = 1;

  static readonly #vector3Temp1: HsVector3 = HsVector3.makeZeroVector();
  static readonly #vector3Temp2: HsVector3 = HsVector3.makeZeroVector();
  static readonly #vector3Temp3: HsVector3 = HsVector3.makeZeroVector();

  static readonly #matrix4Temp1: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  static readonly #matrix4Temp2: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  static readonly #matrix4Temp3: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  static readonly #matrix4Temp4: HsMatrix4 = HsMatrix4.makeIdentityMatrix();

  static readonly #quaternion4Temp1: HsQuaternion = HsQuaternion.makeIdentityQuaternion();
  static readonly #quaternion4Temp2: HsQuaternion = HsQuaternion.makeIdentityQuaternion();
  static readonly #quaternion4Temp3: HsQuaternion = HsQuaternion.makeIdentityQuaternion();

  constructor(
    private readonly camera: HsCamera,
    private readonly controlActionsMap: Map<HsControlActionType, HsControlAction>,
    private readonly canvasSize: HsCanvasSize | null
  ) {}

  public processControlActions(): void {
    this.frameStartTime = this.frameEndTime;
    this.frameEndTime = Date.now();
    this.deltaTime = this.frameStartTime === null ? this.deltaTime : this.frameEndTime - this.frameStartTime;

    const controlActions: IterableIterator<HsControlAction> = this.controlActionsMap.values();
    for (let index: number = 0; index < this.controlActionsMap.size; index++) {
      const element: IteratorResult<HsControlAction> = controlActions.next();
      const controlAction: HsControlAction | undefined = element.value;

      if (controlAction === undefined || !controlAction.isActive) {
        continue;
      }

      if (controlAction instanceof HsScrollControlAction) {
        this.processScrollControlAction(controlAction);
        continue;
      }

      if (controlAction instanceof HsZoomControlAction) {
        this.processZoomControlAction(controlAction);
        continue;
      }

      if (controlAction instanceof HsMouseMoveControlAction) {
        this.processMoveControlAction(controlAction);
        continue;
      }
    }
  }

  private processScrollControlAction(scrollActon: HsScrollControlAction): void {
    const { scrollDelta }: HsScrollControlAction = scrollActon;

    const cameraMatrix: HsMatrix4 = HsCameraRotationAroundSelectedPoint.#matrix4Temp1.copy(this.camera.worldMatrix);
    const cameraPosition: HsVector3 = HsCameraRotationAroundSelectedPoint.#vector3Temp2.setFromMatrixPosition(
      this.camera.worldMatrix.values
    );

    HsCameraRotationAroundSelectedPoint.#vector3Temp3.copy(cameraPosition).subtract(this.rotationPoint);
    const currentDistance: number = HsCameraRotationAroundSelectedPoint.#vector3Temp3.length();

    if (currentDistance < CAMERA_TO_TARGET_MIN_DISTANCE && scrollDelta < 0) {
      return;
    }

    HsCameraRotationAroundSelectedPoint.#vector3Temp3.normalize();

    const progressiveDelta: number =
      scrollDelta < 0
        ? scrollDelta / 100 - Math.abs(currentDistance) / 10
        : scrollDelta / 100 + Math.abs(currentDistance) / 10;
    const newDistance: number = currentDistance + progressiveDelta < 0.5 ? 0 : progressiveDelta;

    HsCameraRotationAroundSelectedPoint.#vector3Temp3.multiplyByScalar(newDistance);
    cameraPosition.add(HsCameraRotationAroundSelectedPoint.#vector3Temp3);
    cameraMatrix.setPosition(cameraPosition.values);

    hsApplyTransformationToCamera(this.camera, cameraMatrix);
  }

  private processZoomControlAction(zoomAction: HsZoomControlAction): void {
    const cameraMatrix: HsMatrix4 = HsCameraRotationAroundSelectedPoint.#matrix4Temp1.copy(this.camera.worldMatrix);
    const cameraPosition: HsVector3 = HsCameraRotationAroundSelectedPoint.#vector3Temp2.setFromMatrixPosition(
      this.camera.worldMatrix.values
    );
    const offset: HsVector3 = HsCameraRotationAroundSelectedPoint.#vector3Temp3
      .copy(cameraPosition)
      .subtract(this.rotationPoint);
    const currentDistance: number = offset.length();
    if (currentDistance === 0 || (currentDistance <= CAMERA_TO_TARGET_MIN_DISTANCE && zoomAction.scale > 1)) {
      return;
    }

    // Orbit tools zoom toward their rotation center rather than the point under the fingers.
    const nextDistance: number = Math.max(currentDistance / zoomAction.scale, CAMERA_TO_TARGET_MIN_DISTANCE);
    offset.multiplyByScalar(nextDistance / currentDistance);
    cameraPosition.copy(this.rotationPoint).add(offset);
    cameraMatrix.setPosition(cameraPosition.values);

    hsApplyTransformationToCamera(this.camera, cameraMatrix);
  }

  private processMoveControlAction(moveAction: HsMouseMoveControlAction): void {
    switch (moveAction.actionType) {
      case HsControlActionType.LeftMove:
        this.rotateCameraOnLeftMoveAction(moveAction);
        return;
      default:
        return;
    }
  }

  private rotateCameraOnLeftMoveAction(moveActon: HsMouseMoveControlAction): void {
    const renderCanvasRatio: number = this.getRenderCanvasRatio();
    const rotationSpeed: number =
      HsToolsConstants.MOUSE_MOVE_ROTATE_FACTOR * HsHypersceneConstants.DEG_2_RAD * renderCanvasRatio * this.deltaTime;
    const actionDeltaY: number = moveActon.delta.y;
    const rotationDeltaAroundX: number = actionDeltaY * rotationSpeed;
    const actionDeltaX: number = moveActon.delta.x;
    const rotationDeltaAroundY: number = actionDeltaX * rotationSpeed;

    const cameraMatrix: HsMatrix4 = HsCameraRotationAroundSelectedPoint.#matrix4Temp1.copy(this.camera.worldMatrix);
    cameraMatrix.subtractPosition(this.rotationPoint.values);
    const cameraRotation: HsMatrix4 = HsCameraRotationAroundSelectedPoint.#matrix4Temp2
      .setIdentityValues()
      .copyRotation(this.camera.worldMatrix);

    const axisOnXZPlane: HsVector3 = HsCameraRotationAroundSelectedPoint.#vector3Temp1
      .set(1, 0, 0)
      .applyMatrix4(cameraRotation.values)
      .normalize();
    axisOnXZPlane.values[1] = 0;

    const deltaQuaternionXZ: HsQuaternion = HsCameraRotationAroundSelectedPoint.#quaternion4Temp1.setFromAxisAngle(
      axisOnXZPlane.values,
      rotationDeltaAroundX
    );

    const deltaQuaternionY: HsQuaternion = HsCameraRotationAroundSelectedPoint.#quaternion4Temp2.setFromAxisAngle(
      [0, 1, 0],
      rotationDeltaAroundY
    );

    const deltaQuaternion: HsQuaternion = HsCameraRotationAroundSelectedPoint.#quaternion4Temp3.copy(deltaQuaternionY);
    deltaQuaternion.multiply(deltaQuaternionXZ);
    const deltaMatrix: HsMatrix4 = HsCameraRotationAroundSelectedPoint.#matrix4Temp2.compose(
      [0, 0, 0],
      deltaQuaternion.values,
      [1, 1, 1]
    );

    const cameraMatrixBeforeMultiplication: HsMatrix4 =
      HsCameraRotationAroundSelectedPoint.#matrix4Temp3.copy(cameraMatrix);
    cameraMatrix.multiplyMatrices(deltaMatrix, cameraMatrix);
    cameraMatrix.addTransition(this.rotationPoint.values);

    const cameraRotationToCheck: HsMatrix4 = HsCameraRotationAroundSelectedPoint.#matrix4Temp4
      .copyRotation(cameraMatrix)
      .inverse();
    const slantVector: HsVector3 = HsCameraRotationAroundSelectedPoint.#vector3Temp1
      .set(0, 1, 0)
      .applyMatrix4(cameraRotationToCheck.values);

    if (slantVector.y < 0) {
      cameraMatrixBeforeMultiplication.multiplyMatrices(
        deltaMatrix.compose([0, 0, 0], deltaQuaternionY.values, [1, 1, 1]),
        cameraMatrixBeforeMultiplication
      );
      cameraMatrix.copy(cameraMatrixBeforeMultiplication);
      cameraMatrix.addTransition(this.rotationPoint.values);
    }

    hsApplyTransformationToCamera(this.camera, cameraMatrix);
  }

  private getRenderCanvasRatio(): number {
    const newSize: HsCanvasSize | null = this.canvasSize;
    if (newSize !== null && newSize.renderWidth < newSize.realWidth) {
      return newSize.renderWidth / newSize.realWidth;
    }
    return 1;
  }
}
